import { APP_ENV, ROKT_ACCOUNT_ID } from "@shared/env";
import { loadRokt } from "@shared/lib/rokt/loader";
import {
  RoktClientBlockedError,
  type RoktLauncherInstance,
} from "@shared/lib/rokt/types/rokt-types";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import { useEffect, useState } from "react";
import { RoktLauncherProps } from "./RoktLauncherProps";

export function RoktLauncher(props: RoktLauncherProps): JSX.Element {
  const [roktLauncher, setRoktLauncher] = useState<RoktLauncherInstance | null>(
    null
  );
  const [roktStatus, setRoktStatus] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");

  const {
    transactionId,
    email,
    firstName,
    lastName,
    mobile,
    country,
    billingZipCode,
    language,
    eventVenue,
  } = props;

  useEffect(() => {
    if (!ROKT_ACCOUNT_ID) {
      logAppInfo("Rokt account ID not configured", {
        category: "RoktLauncher",
        level: "warning",
      });
      setRoktStatus("error");
      return;
    }

    const sandbox = APP_ENV !== "production";
    setRoktStatus("loading");

    loadRokt(ROKT_ACCOUNT_ID, sandbox)
      .then((launcher) => {
        if (launcher) {
          setRoktLauncher(launcher);
          setRoktStatus("loaded");

          logAppInfo("Rokt initialized successfully", {
            category: "RoktLauncher",
            data: { accountId: ROKT_ACCOUNT_ID },
          });
        } else {
          // Silently handle client-side blocking - no logging needed
          setRoktStatus("error");
        }
      })
      .catch((error) => {
        setRoktStatus("error");
        setRoktLauncher(null);

        // Only log/record actual errors, silently ignore client-side blocking
        if (!(error instanceof RoktClientBlockedError)) {
          const roktError = new Error(
            `Rokt initialization failed: ${error.message || error}`
          );

          logAppInfo("Rokt initialization failed", {
            category: "RoktLauncher",
            level: "error",
            data: {
              accountId: ROKT_ACCOUNT_ID,
              sandbox,
              environment: APP_ENV,
              originalError: String(error),
            },
          });

          recordAppError(roktError);
        }
      });
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (roktLauncher && transactionId && email && roktStatus === "loaded") {
      const attributes = {
        email,
        confirmationref: transactionId,
        firstname: firstName,
        lastname: lastName,
        mobile,
        country,
        billingzipcode: billingZipCode,
        language,
        eventvenue: eventVenue,
      };

      try {
        const selectionPromise = roktLauncher.selectPlacements({
          attributes,
          identifier: "confirmation",
        });

        selectionPromise
          .then(() => {
            logAppInfo("Rokt placement successful", {
              category: "RoktLauncher",
              data: { transactionId },
            });
          })
          .catch((placementError) => {
            if (String(placementError).includes("PLUGIN_ALREADY_APPENDED")) {
              logAppInfo("Rokt plugin already appended (harmless)", {
                category: "RoktLauncher",
                level: "info",
                data: { transactionId },
              });
              return;
            }

            const error = new Error(`Rokt placement failed: ${placementError}`);

            logAppInfo("Rokt placement failed", {
              category: "RoktLauncher",
              level: "error",
              data: {
                transactionId,
                originalError: String(placementError),
              },
            });

            recordAppError(error);
          });

        return () => {
          selectionPromise
            .then((selection) => selection.close())
            .catch((closeError) => {
              logAppInfo("Error closing Rokt placement", {
                category: "RoktLauncher",
                level: "warning",
                data: {
                  transactionId,
                  error: String(closeError),
                },
              });
            });
        };
      } catch (syncError) {
        const error = new Error(`Rokt placement sync error: ${syncError}`);

        logAppInfo("Rokt placement sync error", {
          category: "RoktLauncher",
          level: "error",
          data: {
            transactionId,
            originalError: String(syncError),
          },
        });

        recordAppError(error);
      }
    }
  }, [
    roktLauncher,
    transactionId,
    roktStatus,
    email,
    firstName,
    lastName,
    mobile,
    billingZipCode,
    country,
    language,
    eventVenue,
  ]);

  return (
    <div
      data-testid="rokt-launcher"
      id="rokt-placeholder"
      style={{ display: "none" }}
    />
  );
}
